/* ============================================================
   HOME PAGE — Цифровой ДИАК ЗФ
   Главная страница: амбиция, плитки проектов, интерактивный ганта.
   Design: Арктический Командный Центр
   ============================================================ */

import { useMemo, useState, useEffect } from "react";
import { Link } from "wouter";
import type { DraggableAttributes, DraggableSyntheticListeners } from "@dnd-kit/core";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ClipboardCheck, Search, BarChart3, FileText, Package,
  FileSignature, Calculator, TrendingUp, LineChart,
  FolderOpen, Users, Building2, Bot, Clock,
  GripVertical, Trash2, Pencil, Plus,
} from "lucide-react";
import { toast } from "sonner";
import { AMBITION, GANTT_ITEMS, type GanttItem } from "@/data/projects";
import { filterProjectsByMainGantt } from "@/lib/mainGanttProjectSpan";
import { phaseOptionsFromRoadmap } from "@/data/phaseOptions";
import { useProjects } from "@/contexts/ProjectContext";
import {
  type EditableProject,
  type RoadmapPhase,
  compareCoreProjectsInPhase,
  roadmapPhasesSorted,
  phaseLabelFromMeta,
} from "@/store/projectStore";
import GanttChart from "@/components/GanttChart";
import { DocumentsSection } from "@/components/DocumentsSection";
import { useGlobalDocuments } from "@/hooks/useGlobalDocuments";
import Sidebar from "@/components/Sidebar";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const HERO_IMG =
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663488115797/HZnZgLCcEJPccQuKCSa6ZJ/diak-hero-banner-Ye8jYPpp4Mc8vyXc3PcwsE.webp";

const iconMap: Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>> = {
  ClipboardCheck, Search, BarChart3, FileText, Package,
  FileSignature, Calculator, TrendingUp, LineChart,
  FolderOpen, Users, Building2,
};

const ambitionIconMap: Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>> = {
  TrendingUp, Users, Bot, Clock,
};

const STATUS_LABELS: Record<string, string> = {
  active: "В работе",
  planned: "Запланировано",
  done: "Завершено",
};

const AI_LABELS: Record<string, string> = {
  high: ">80%",
  medium: "50–80%",
  needs_analysis: "Требует проработки",
};

const AI_COLORS: Record<string, string> = {
  high: "#00A651",
  medium: "#F5A623",
  needs_analysis: "#8E9BAE",
};

/**
 * Профильные (core) проекты для сетки этапа: полоса на ганте в этом этапе или назначение на этап без полосы.
 * Непрофильные остаются только в блоке «Непрофильные задачи», даже если на ганте у них этап 3.
 */
function projectsForPhaseTiles(
  onRoadmap: EditableProject[],
  phaseId: string,
  ganttItems: GanttItem[],
  fallback: GanttItem[],
): EditableProject[] {
  const source = ganttSourceForHome(ganttItems, fallback);
  const barProjectIds = new Set(
    source.filter((g) => g.phaseId === phaseId).map((g) => g.projectId),
  );
  return onRoadmap.filter(
    (p) =>
      p.group === "core" &&
      (barProjectIds.has(p.id) ||
        (p.phaseId === phaseId && !barProjectIds.has(p.id))),
  );
}

/** Карточка этапа: одна строка общего ганта или «осиротевший» проект без полосы в этапе. */
type CorePhaseTileItem = {
  project: EditableProject;
  index: number;
  sortableId: string;
  /** Как подпись на полосе ганта (ОВСС / Ремонты и т.д.) */
  listTitle?: string;
  ganttItemId?: string;
};

function ganttSourceForHome(ganttItems: GanttItem[], fallback: GanttItem[]): GanttItem[] {
  return ganttItems.length > 0 ? ganttItems : fallback;
}

function buildCorePhaseTileItems(
  plist: EditableProject[],
  phaseId: string,
  ganttItems: GanttItem[],
  fallback: GanttItem[],
  startIndex: number,
): { items: CorePhaseTileItem[]; nextIndex: number } {
  const source = ganttSourceForHome(ganttItems, fallback);
  const allowed = new Set(plist.map((p) => p.id));
  const ganttRows: GanttItem[] = [];
  for (const g of source) {
    if (g.phaseId === phaseId && allowed.has(g.projectId)) {
      ganttRows.push(g);
    }
  }
  const covered = new Set(ganttRows.map((g) => g.projectId));
  const orphans = plist.filter((p) => !covered.has(p.id)).sort(compareCoreProjectsInPhase);
  let idx = startIndex;
  const items: CorePhaseTileItem[] = [];
  for (const g of ganttRows) {
    const project = plist.find((p) => p.id === g.projectId);
    if (!project) continue;
    items.push({
      project,
      index: idx++,
      sortableId: g.id,
      listTitle: g.title,
      ganttItemId: g.id,
    });
  }
  for (const p of orphans) {
    items.push({
      project: p,
      index: idx++,
      sortableId: `orphan-${p.id}`,
      ganttItemId: undefined,
    });
  }
  return { items, nextIndex: idx };
}

function cardCountWordRu(n: number): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m100 >= 11 && m100 <= 14) return "карточек";
  if (m10 === 1) return "карточка";
  if (m10 >= 2 && m10 <= 4) return "карточки";
  return "карточек";
}

type SortableHandleBind = {
  setActivatorNodeRef: (element: HTMLElement | null) => void;
  listeners: DraggableSyntheticListeners;
  attributes: DraggableAttributes;
};

function ProjectTile({
  project,
  index,
  sortableHandle,
  listTitle,
}: {
  project: EditableProject;
  index: number;
  sortableHandle?: SortableHandleBind;
  /** Заголовок с полосы ганта; ссылка ведёт на тот же паспорт проекта. */
  listTitle?: string;
}) {
  const { updateProject, canEdit, roadmapPhases, removeProject } = useProjects();
  const phaseOpts = phaseOptionsFromRoadmap(roadmapPhases);
  const phaseKnown = phaseOpts.some((o) => o.value === project.phaseId);
  const Icon = iconMap[project.icon] || FileText;
  const isCore = project.group === "core";

  const onDeleteProject = () => {
    if (!canEdit || !isCore) return;
    if (!window.confirm(`Удалить проект «${project.title}» и все его полосы на ганте? Действие необратимо.`)) {
      return;
    }
    removeProject(project.id);
  };

  return (
    <div
      className={cn("nn-tile tile-animate h-full flex flex-col relative", project.status)}
      style={{ animationDelay: `${index * 50}ms` }}
    >
      {canEdit && isCore ? (
        <button
          type="button"
          onClick={onDeleteProject}
          className="absolute right-2 top-2 z-10 rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          aria-label="Удалить проект"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      ) : null}
      <div className="flex gap-2 p-4 pb-2 flex-1 min-h-0">
        {sortableHandle ? (
          <button
            type="button"
            ref={sortableHandle.setActivatorNodeRef}
            {...sortableHandle.listeners}
            {...sortableHandle.attributes}
            className="mt-0.5 flex-shrink-0 touch-none rounded-md p-1 text-muted-foreground hover:bg-muted/80 cursor-grab active:cursor-grabbing"
            aria-label="Изменить порядок карточки"
          >
            <GripVertical className="w-4 h-4" />
          </button>
        ) : null}
        <Link href={`/project/${project.id}`} className="block flex-1 min-w-0 min-h-0 cursor-pointer pr-8">
          <div className="flex items-start justify-between mb-3 gap-2">
            <div
              className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0"
              style={{ background: "var(--nn-blue-pale)" }}
            >
              <Icon className="w-[18px] h-[18px]" style={{ color: "var(--nn-blue)" } as React.CSSProperties} />
            </div>
            <span className={`status-${project.status} flex-shrink-0`}>{STATUS_LABELS[project.status]}</span>
          </div>

          <div className="font-bold text-sm mb-1.5 leading-snug" style={{ color: "var(--nn-dark)" }}>
            {listTitle ?? project.title}
          </div>
          {listTitle != null && listTitle !== project.title ? (
            <div className="text-[10px] mb-2 leading-tight" style={{ color: "var(--muted-foreground)" }}>
              В каталоге: {project.shortTitle}
            </div>
          ) : null}

          <div className="text-xs mb-3 line-clamp-2" style={{ color: "var(--muted-foreground)" }}>
            {project.description}
          </div>

          <div className="flex items-center mt-auto pt-2 border-t" style={{ borderColor: "var(--border)" }}>
            <div className="flex items-center gap-1">
              <Bot className="w-3 h-3" style={{ color: AI_COLORS[project.aiReadiness] }} />
              <span className="text-xs font-semibold" style={{ color: AI_COLORS[project.aiReadiness] }}>
                ИИ: {AI_LABELS[project.aiReadiness]}
              </span>
            </div>
          </div>
        </Link>
      </div>

      {isCore ? (
        <div
          className="px-4 pb-2"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <label
            className="text-[10px] font-semibold uppercase tracking-wide block mb-1"
            style={{ color: "var(--muted-foreground)" }}
          >
            Этап дорожной карты
          </label>
          {phaseOpts.length === 0 ? (
            <p className="text-xs text-muted-foreground">Этапы не заданы</p>
          ) : (
            <Select
              value={project.phaseId}
              onValueChange={(v) => {
                if (v === project.phaseId) return;
                const meta = roadmapPhases.find((p) => p.id === v);
                updateProject(project.id, {
                  phaseId: v,
                  phaseLabel: meta ? phaseLabelFromMeta(meta) : "",
                });
              }}
              disabled={!canEdit}
            >
              <SelectTrigger
                size="sm"
                className="h-8 w-full min-w-0 text-xs"
                aria-label="Этап проекта"
              >
                <SelectValue placeholder="Выберите этап" />
              </SelectTrigger>
              <SelectContent position="popper" align="start" className="z-[100] max-h-72">
                {!phaseKnown ? (
                  <SelectItem value={project.phaseId} className="text-xs">
                    {project.phaseLabel || project.phaseId}
                  </SelectItem>
                ) : null}
                {phaseOpts.map((o) => (
                  <SelectItem key={o.value} value={o.value} className="text-xs">
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      ) : null}

      <Link href={`/project/${project.id}`} className="block px-4 pb-4 pt-1 cursor-pointer">
        <div className="mt-1">
          <div className="flex justify-between text-xs mb-0.5" style={{ color: "var(--muted-foreground)" }}>
            <span>Прогресс по плану</span>
            <span className="tabular-nums">{project.progress}%</span>
          </div>
          <div className="w-full h-1 rounded-full" style={{ background: "var(--border)" }}>
            <div
              className="h-1 rounded-full progress-animate"
              style={{ width: `${project.progress}%`, background: "var(--nn-blue)" }}
            />
          </div>
        </div>
      </Link>
    </div>
  );
}

const PHASE_GRID_CLASS =
  "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4";

function SortableProjectTile({
  project,
  index,
  sortableId,
  listTitle,
}: {
  project: EditableProject;
  index: number;
  sortableId: string;
  listTitle?: string;
}) {
  const {
    setNodeRef,
    setActivatorNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn("h-full", isDragging && "relative z-20 opacity-95 shadow-xl rounded-lg")}
    >
      <ProjectTile
        project={project}
        index={index}
        listTitle={listTitle}
        sortableHandle={{
          setActivatorNodeRef,
          listeners,
          attributes,
        }}
      />
    </div>
  );
}

function CorePhaseProjectGrid({
  phaseId,
  items,
}: {
  phaseId: string;
  items: CorePhaseTileItem[];
}) {
  const { canEdit, reorderCoreProjectsInPhase, reorderGanttItemsForPhase } = useProjects();
  const ids = useMemo(() => items.map((i) => i.sortableId), [items]);
  const allGanttBacked = items.length > 0 && items.every((i) => i.ganttItemId != null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const newOrderIds = arrayMove(ids, oldIndex, newIndex);

    const projectOrderUnique: string[] = [];
    const seen = new Set<string>();
    for (const sid of newOrderIds) {
      const it = items.find((x) => x.sortableId === sid);
      if (!it) continue;
      if (!seen.has(it.project.id)) {
        seen.add(it.project.id);
        projectOrderUnique.push(it.project.id);
      }
    }

    if (allGanttBacked) {
      reorderGanttItemsForPhase(phaseId, newOrderIds);
    }
    reorderCoreProjectsInPhase(phaseId, projectOrderUnique);
  };

  if (!canEdit) {
    return (
      <div className={PHASE_GRID_CLASS}>
        {items.map((item) => (
          <ProjectTile
            key={item.sortableId}
            project={item.project}
            index={item.index}
            listTitle={item.listTitle}
          />
        ))}
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={rectSortingStrategy}>
        <div className={PHASE_GRID_CLASS}>
          {items.map((item) => (
            <SortableProjectTile
              key={item.sortableId}
              sortableId={item.sortableId}
              project={item.project}
              index={item.index}
              listTitle={item.listTitle}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function RoadmapPhaseEditDialog({
  phase,
  open,
  onOpenChange,
  onSave,
}: {
  phase: RoadmapPhase | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSave: (id: string, patch: Partial<Omit<RoadmapPhase, "id">>) => void;
}) {
  const [label, setLabel] = useState("");
  const [period, setPeriod] = useState("");
  const [title, setTitle] = useState("");
  const [color, setColor] = useState("#0055A5");
  const [order, setOrder] = useState(1);

  useEffect(() => {
    if (phase) {
      setLabel(phase.label);
      setPeriod(phase.period);
      setTitle(phase.title ?? "");
      setColor(phase.color);
      setOrder(phase.order);
    }
  }, [phase]);

  const submit = () => {
    if (!phase) return;
    onSave(phase.id, {
      label,
      period,
      title: title.trim() ? title.trim() : undefined,
      color,
      order: Number(order) || 1,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Редактировать этап</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 py-2">
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Подпись</label>
            <input
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Период</label>
            <input
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Заголовок (опционально)</label>
            <input
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Цвет</label>
              <input
                type="color"
                className="mt-1 h-9 w-full cursor-pointer rounded-md border border-border bg-background"
                value={color}
                onChange={(e) => setColor(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Порядок</label>
              <input
                type="number"
                className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                value={order}
                onChange={(e) => setOrder(Number(e.target.value))}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button type="button" onClick={submit}>
            Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Home() {
  const {
    projects,
    ganttItems,
    workspaceLoading,
    roadmapPhases,
    canEdit,
    addRoadmapPhase,
    updateRoadmapPhase,
    removeRoadmapPhase,
    addCoreProject,
  } = useProjects();
  const [editingPhase, setEditingPhase] = useState<RoadmapPhase | null>(null);
  const [phaseDialogOpen, setPhaseDialogOpen] = useState(false);

  const openEditPhase = (p: RoadmapPhase) => {
    setEditingPhase(p);
    setPhaseDialogOpen(true);
  };

  const projectsOnRoadmap = useMemo(
    () => filterProjectsByMainGantt(projects, ganttItems, GANTT_ITEMS),
    [projects, ganttItems],
  );
  const supportProjects = projectsOnRoadmap.filter((p) => p.group === "support");

  const corePhaseBlocks = useMemo(() => {
    const sorted = roadmapPhasesSorted(roadmapPhases);
    let idx = 0;
    return sorted.map((meta) => {
      const plist = projectsForPhaseTiles(projectsOnRoadmap, meta.id, ganttItems, GANTT_ITEMS);
      const { items, nextIndex } = buildCorePhaseTileItems(plist, meta.id, ganttItems, GANTT_ITEMS, idx);
      idx = nextIndex;
      return { meta, items };
    });
  }, [projectsOnRoadmap, roadmapPhases, ganttItems]);

  const projectIdsInRoadmapPhaseGrids = useMemo(() => {
    const s = new Set<string>();
    for (const b of corePhaseBlocks) {
      for (const it of b.items) {
        s.add(it.project.id);
      }
    }
    return s;
  }, [corePhaseBlocks]);

  const supportProjectsNotInPhaseGrids = useMemo(
    () => supportProjects.filter((p) => !projectIdsInRoadmapPhaseGrids.has(p.id)),
    [supportProjects, projectIdsInRoadmapPhaseGrids],
  );

  const roadmapPhaseTileCount = useMemo(
    () => corePhaseBlocks.reduce((acc, b) => acc + b.items.length, 0),
    [corePhaseBlocks],
  );

  const sortedPills = useMemo(() => roadmapPhasesSorted(roadmapPhases), [roadmapPhases]);

  return (
    <div className="flex min-h-screen" style={{ background: "var(--background)" }}>
      <Sidebar />

      <RoadmapPhaseEditDialog
        phase={editingPhase}
        open={phaseDialogOpen}
        onOpenChange={(o) => {
          setPhaseDialogOpen(o);
          if (!o) setEditingPhase(null);
        }}
        onSave={(id, patch) => updateRoadmapPhase(id, patch)}
      />

      {/* Main content */}
      <main className="relative min-h-screen flex-1 transition-[margin-left] duration-200 ease-out [margin-left:var(--app-sidebar-width)]">
        {workspaceLoading ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80 text-sm text-muted-foreground">
            Загрузка данных…
          </div>
        ) : null}
        {/* Hero banner */}
        <div
          className="relative w-full overflow-hidden"
          style={{ height: 180, background: "#EBF3FF" }}
        >
          <img
            src={HERO_IMG}
            alt="ДИАК ЗФ"
            className="w-full h-full object-cover object-center"
            style={{ opacity: 0.55, transform: "scale(1.6)" }}
          />
          <div className="absolute inset-0 flex flex-col justify-center px-8">
            <div className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: "var(--nn-blue)" }}>
              Норникель · Заполярный Филиал
            </div>
            <h1 className="text-2xl font-bold mb-1" style={{ color: "var(--nn-dark)" }}>
              Цифровой ДИАК ЗФ
            </h1>
            <p className="text-sm max-w-xl" style={{ color: "oklch(0.35 0.04 250)" }}>
              {AMBITION.headline}
            </p>
          </div>
        </div>

        {/* Ambition metrics */}
        <div className="px-6 py-4 border-b" style={{ borderColor: "var(--border)", background: "white" }}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {AMBITION.effects.map((effect) => {
              const Icon = ambitionIconMap[effect.icon] || TrendingUp;
              return (
                <div key={effect.label} className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
                    style={{ background: "var(--nn-blue-pale)" }}
                  >
                    <Icon className="w-4 h-4" style={{ color: "var(--nn-blue)" } as React.CSSProperties} />
                  </div>
                  <div>
                    <div className="font-bold text-base leading-tight" style={{ color: "var(--nn-blue)" }}>
                      {effect.value}
                    </div>
                    <div className="text-xs leading-tight" style={{ color: "var(--muted-foreground)" }}>
                      {effect.label}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="px-6 py-6">
          {/* Roadmap phases summary */}
          <div className="flex gap-3 mb-6 flex-wrap items-center">
            {sortedPills.map((phase) => (
              <div
                key={phase.id}
                className="group relative flex items-center gap-2 pl-3 pr-10 py-1.5 rounded-full text-xs font-semibold border"
                style={{
                  borderColor: phase.color,
                  color: phase.color,
                  background: `${phase.color}10`,
                }}
              >
                <span>{phase.label}</span>
                <span className="opacity-70 font-normal">{phase.period}</span>
                {canEdit ? (
                  <span className="absolute right-1 top-1/2 flex -translate-y-1/2 gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                    <button
                      type="button"
                      className="rounded p-1 hover:bg-background/80"
                      aria-label="Изменить этап"
                      onClick={() => openEditPhase(phase)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      className="rounded p-1 hover:bg-destructive/15 text-destructive"
                      aria-label="Удалить этап"
                      onClick={() => {
                        const label = (phase.label || "").trim() || "этот этап";
                        if (!window.confirm(`Удалить этап «${label}» с дорожной карты?\n\nДействие необратимо.`)) {
                          return;
                        }
                        try {
                          removeRoadmapPhase(phase.id);
                        } catch (e) {
                          toast.error(e instanceof Error ? e.message : "Нельзя удалить этап");
                        }
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ) : null}
              </div>
            ))}
            {canEdit ? (
              <Button type="button" variant="outline" size="sm" className="h-8 rounded-full text-xs" onClick={addRoadmapPhase}>
                <Plus className="h-3.5 w-3.5 mr-1" />
                Этап
              </Button>
            ) : null}
          </div>

          {/* Core projects — по этапам дорожной карты */}
          <section className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold" style={{ color: "var(--nn-dark)" }}>
                  Профильные задачи
                </h2>
                <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>
                  По каждому этапу — карточки профильных инициатив по строкам общего ганта (в т.ч. если полоса этапа
                  задана при другом выбранном этапе в паспорте). Смена этапа на карточке обновляет полосы на ганте
                  ниже.
                </p>
              </div>
            </div>

            <div className="space-y-10">
              {corePhaseBlocks.map(({ meta, items }) => (
                <div key={meta.id}>
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <div
                      className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border"
                      style={{
                        borderColor: meta.color,
                        color: meta.color,
                        background: `${meta.color}10`,
                      }}
                    >
                      <span>{meta.label}</span>
                      <span className="opacity-70 font-normal">{meta.period}</span>
                    </div>
                    <span className="text-sm" style={{ color: "var(--muted-foreground)" }}>
                      {items.length} {cardCountWordRu(items.length)}
                    </span>
                    {canEdit ? (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="h-8 text-xs"
                        onClick={() => addCoreProject(meta.id)}
                      >
                        <Plus className="h-3.5 w-3.5 mr-1" />
                        Проект
                      </Button>
                    ) : null}
                  </div>
                  {items.length === 0 ? (
                    <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>
                      Нет проектов в этом этапе. Добавьте проект кнопкой выше или перенесите карточку селектором «Этап дорожной карты».
                    </p>
                  ) : (
                    <CorePhaseProjectGrid phaseId={meta.id} items={items} />
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Support projects */}
          <section className="mb-10">
            <div className="mb-4">
              <h2 className="text-lg font-bold" style={{ color: "var(--nn-dark)" }}>
                Непрофильные задачи
              </h2>
              <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>
                Поддерживающие процессы — автоматизация в Этапе 3
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {supportProjectsNotInPhaseGrids.map((project, i) => (
                <ProjectTile key={project.id} project={project} index={roadmapPhaseTileCount + i} />
              ))}
            </div>
          </section>

          {/* Gantt chart */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold" style={{ color: "var(--nn-dark)" }}>
                  Дорожная карта цифровизации
                </h2>
                <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>
                  Переход на цифровой департамент в три этапа к Q4 2027
                </p>
              </div>
            </div>
            <div
              className="rounded-lg border p-5"
              style={{ background: "white", borderColor: "var(--border)" }}
            >
              <GanttChart />
            </div>
          </section>

          {/* Документы ДИАКа — глобальный архив под общим Гантом */}
          <GlobalDocumentsBlock />
        </div>
      </main>
    </div>
  );
}

/**
 * Раздел «Документы ДИАКа» на главной странице: общий архив регламентов,
 * методологий, шаблонов и т.п. Настройку структуры (группы/типы) могут делать
 * admin и editor прямо из этого же блока — без правок UI-кода.
 */
function GlobalDocumentsBlock() {
  const { canSeeDocuments } = useProjects();
  const { store, loading, applyChange, canEdit } = useGlobalDocuments();
  if (!canSeeDocuments) return null; // просмотрщику документы не показываем
  return (
    <section>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold" style={{ color: "var(--nn-dark)" }}>
            Документы ДИАКа
          </h2>
          <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>
            Регламенты, методологии, шаблоны и решения для цифрового ДИАКа. Структуру разделов
            и бейджей-типов можно править прямо здесь — кнопка «Настроить структуру».
          </p>
        </div>
      </div>
      <div
        className="rounded-lg border p-5"
        style={{ background: "white", borderColor: "var(--border)" }}
      >
        {loading ? (
          <p className="py-4 text-center text-xs text-muted-foreground">Загрузка документов…</p>
        ) : (
          <DocumentsSection
            scope="global"
            storageProjectId="__global__"
            documents={store.documents}
            config={store.config}
            onChange={applyChange}
            readOnly={!canEdit}
          />
        )}
      </div>
    </section>
  );
}
